
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Users, Mail, UserPlus, UserMinus, Shield, Crown } from "lucide-react";

interface OrganizationMember {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member';
  joined_at: string;
  profiles: {
    full_name: string;
    email: string;
  } | null;
}

interface SeatManagementProps {
  organizationId: string;
  maxSeats: number;
  currentUserRole: string;
  onMembersUpdate: () => Promise<void>;
}

export const SeatManagement = ({ 
  organizationId, 
  maxSeats, 
  currentUserRole, 
  onMembersUpdate 
}: SeatManagementProps) => {
  const { toast } = useToast();
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadMembers = async () => {
    try {
      // First get the memberships
      const { data: memberships, error: membershipsError } = await supabase
        .from('organization_memberships')
        .select('id, user_id, role, joined_at')
        .eq('organization_id', organizationId)
        .order('joined_at', { ascending: true });

      if (membershipsError) throw membershipsError;

      if (!memberships || memberships.length === 0) {
        setMembers([]);
        return;
      }

      // Get user profiles for all members
      const userIds = memberships.map(m => m.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Combine the data
      const transformedMembers: OrganizationMember[] = memberships.map(membership => {
        const profile = profiles?.find(p => p.id === membership.user_id);
        return {
          id: membership.id,
          user_id: membership.user_id,
          role: membership.role as 'owner' | 'admin' | 'member',
          joined_at: membership.joined_at,
          profiles: profile ? {
            full_name: profile.full_name || '',
            email: profile.email || ''
          } : null
        };
      });
      
      setMembers(transformedMembers);
    } catch (error) {
      console.error('Error loading members:', error);
      toast({
        title: "Error",
        description: "Failed to load team members",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [organizationId]);

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) return;

    try {
      setIsInviting(true);
      
      // Check if we're at the seat limit
      if (members.length >= maxSeats) {
        toast({
          title: "Seat Limit Reached",
          description: `Your plan allows up to ${maxSeats} seats. Upgrade to add more members.`,
          variant: "destructive",
        });
        return;
      }

      // Here you would typically send an invitation email
      // For now, we'll just show a success message
      toast({
        title: "Invitation Sent",
        description: `Invitation sent to ${inviteEmail}`,
      });
      
      setInviteEmail("");
      await onMembersUpdate();
    } catch (error) {
      console.error('Error inviting member:', error);
      toast({
        title: "Error",
        description: "Failed to send invitation",
        variant: "destructive",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    try {
      const { error } = await supabase
        .from('organization_memberships')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: "Member Removed",
        description: `${memberEmail} has been removed from the team`,
      });

      await loadMembers();
      await onMembersUpdate();
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive",
      });
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return <Crown className="h-4 w-4 text-yellow-600" />;
      case 'admin': return <Shield className="h-4 w-4 text-blue-600" />;
      default: return <Users className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-yellow-100 text-yellow-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Team Members ({members.length}/{maxSeats})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Invite New Member */}
        {canManageMembers && (
          <div className="flex gap-2">
            <Input
              placeholder="Enter email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              type="email"
            />
            <Button
              onClick={handleInviteMember}
              disabled={isInviting || !inviteEmail.trim()}
              className="flex items-center gap-2"
            >
              <UserPlus className="h-4 w-4" />
              {isInviting ? "Inviting..." : "Invite"}
            </Button>
          </div>
        )}

        {/* Seat Usage */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">Seat Usage</span>
            <span className="text-sm text-gray-600">{members.length} of {maxSeats} used</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(members.length / maxSeats) * 100}%` }}
            />
          </div>
        </div>

        {/* Members List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-4">Loading members...</div>
          ) : members.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No team members yet. Invite someone to get started!
            </div>
          ) : (
            members.map((member) => (
              <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {getRoleIcon(member.role)}
                    <div>
                      <div className="font-medium">
                        {member.profiles?.full_name || member.profiles?.email || 'Unknown User'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {member.profiles?.email}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={getRoleColor(member.role)}>
                    {member.role}
                  </Badge>
                  {canManageMembers && member.role !== 'owner' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id, member.profiles?.email || 'Unknown')}
                      className="text-red-600 hover:text-red-700"
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Upgrade Prompt */}
        {members.length >= maxSeats && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-orange-600" />
              <span className="font-medium text-orange-800">Seat Limit Reached</span>
            </div>
            <p className="text-sm text-orange-700 mb-3">
              You've reached your {maxSeats}-seat limit. Upgrade your plan to add more team members.
            </p>
            <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100">
              Upgrade Plan
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
